import { cloneElement, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    Plus, Package, Wrench, PencilSimple, Trash, FloppyDisk, Stack,
    Stack as StackIcon, Cube, Plugs,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import FormDialog, { FormDialogCancel } from "../components/FormDialog";
import CopyCodeButton from "../components/CopyCodeButton";
import { CapabilityPicker } from "../components/catalogue/CapabilityPicker";
import { CapabilityBadges } from "../components/catalogue/CapabilityBadges";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
    DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";

const KIND_META = {
    product: { label: "Product", icon: Package, tone: "bg-indigo-50 text-indigo-800 border-indigo-200" },
    service: { label: "Service", icon: Wrench, tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

// Iter C.0 — Phase C Commercial Packaging fixed enums. Kept in sync with
// /app/backend/routes_catalogue.py (FEE_KINDS / ActivationRule). Order
// matters: it's the dropdown order operators see. 10-value enum per
// Rahul's 2026-05-29 directive — customisation, training, integration
// promoted to first-class categories so reporting / forecasting see them
// distinctly rather than accumulated under "Other".
const FEE_KIND_OPTIONS = [
    { value: "implementation", label: "Implementation" },
    { value: "migration",      label: "Migration" },
    { value: "amc",            label: "AMC" },
    { value: "hosting",        label: "Hosting" },
    { value: "support",        label: "Support" },
    { value: "subscription",   label: "Subscription" },
    { value: "customisation",  label: "Customisation" },
    { value: "training",       label: "Training" },
    { value: "integration",    label: "Integration" },
    { value: "other",          label: "Other" },
];
const BILLING_FREQ_OPTIONS = ["One-time", "Monthly", "Quarterly", "Annual"];
const ACTIVATION_RULE_OPTIONS = [
    { value: "immediate",                                label: "Immediate" },
    { value: "go_live",                                   label: "Go-Live date" },
    { value: "project_completion",                        label: "Project completion" },
    { value: "project_completion_plus_free_support",      label: "Project completion + free support window" },
    { value: "project_completion_plus_one_year",          label: "Project completion + 1 year" },
    { value: "manual",                                    label: "Manual activation" },
];
const PRESENTATION_MODE_OPTIONS = [
    { value: "itemized",     label: "Itemized",     hint: "Each component shown as a separate line" },
    { value: "consolidated", label: "Consolidated", hint: "Single bundled line — structure preserved internally" },
    { value: "hybrid",       label: "Hybrid",       hint: "Some components visible, others rolled up" },
];

const EMPTY_COMPONENT = () => ({
    source_catalogue_id: "",
    component_code: "",
    label: "",
    fee_kind: "implementation",
    default_amount: 0,
    billing_frequency: "One-time",
    activation_rule: "immediate",
    free_support_months: null,
    delivery_channels: null,
});

// Iter C.1.2c — Delivery surface options. Multi-select: a component
// typically ships on browser+android+ios for retail wealth platforms,
// while back-office / integration deliverables stay on api or server.
// Empty selection = channel-agnostic (no client-facing channel chip
// rendered in the proposal).
const DELIVERY_CHANNEL_OPTIONS = [
    { value: "browser", short: "B",  label: "Browser" },
    { value: "android", short: "A",  label: "Android" },
    { value: "ios",     short: "i",  label: "iOS"     },
    { value: "api",     short: "AP", label: "API"     },
    { value: "server",  short: "S",  label: "Server"  },
];

// Iter C.0.2 — Auto-derive a unique component_code from a picked master's
// code, disambiguating with _2, _3, ... when the same master is referenced
// more than once in a package. Falls back to the catalogue id when the
// master has no slug-friendly code.
function _deriveComponentCode(master, existingCodes) {
    const base = ((master?.code || master?.id || "comp") + "")
        .toLowerCase().replace(/[^a-z0-9_.-]+/g, "_").replace(/^[_.-]+|[_.-]+$/g, "")
        .slice(0, 28) || "comp";
    if (!existingCodes.includes(base)) return base;
    for (let i = 2; i < 50; i++) {
        const cand = `${base}_${i}`;
        if (!existingCodes.includes(cand)) return cand;
    }
    return base + "_" + Math.random().toString(36).slice(2, 6);
}

// Iter C.0 — Single editable row in the package component table.
// Co-located with the dialog because the row's state is fully owned by
// the parent ItemDialog (it just renders + emits patches via onChange).
function ComponentRow({ idx, comp, onChange, onRemove, atomicMasters, allCodes }) {
    const showFreeMonths = comp.activation_rule === "project_completion_plus_free_support";
    // Restrict activation_rule options to legal values for One-time fees.
    const ruleOptions = comp.billing_frequency === "One-time"
        ? ACTIVATION_RULE_OPTIONS.filter((r) => r.value === "immediate" || r.value === "manual")
        : ACTIVATION_RULE_OPTIONS;
    const linkedMaster = atomicMasters.find((m) => m.id === comp.source_catalogue_id);

    // Iter C.0.2 — When the operator picks a master record from the dropdown
    // we cascade-prefill the dependent fields (code, label, default amount).
    // Operators can still edit any of these inline afterwards — the linkage
    // is what's snapshot-relevant; the local values are the per-package
    // commercial framing.
    const onPickMaster = (newId) => {
        if (!newId) {
            onChange({ source_catalogue_id: "" });
            return;
        }
        const master = atomicMasters.find((m) => m.id === newId);
        if (!master) {
            onChange({ source_catalogue_id: newId });
            return;
        }
        const codesOtherThanThis = allCodes.filter((c, i) => i !== idx);
        const patch = { source_catalogue_id: newId };
        // Only auto-prefill empty fields — never clobber operator edits.
        if (!comp.component_code) {
            patch.component_code = _deriveComponentCode(master, codesOtherThanThis);
        }
        if (!comp.label) {
            patch.label = master.name || "";
        }
        if (!comp.default_amount) {
            patch.default_amount = Number(master.default_software_fee || 0);
        }
        onChange(patch);
    };

    return (
        <tr className="border-b border-slate-100 last:border-b-0" data-testid={`catalogue-component-row-${idx}`}>
            <td className="px-2 py-1.5">
                <select
                    value={comp.source_catalogue_id || ""}
                    onChange={(e) => onPickMaster(e.target.value)}
                    className={`h-7 w-full px-1.5 text-[11px] border rounded bg-white ${
                        comp.source_catalogue_id ? "border-slate-200" : "border-amber-300 bg-amber-50"
                    }`}
                    data-testid={`component-source-${idx}`}
                    title={linkedMaster ? `Linked to ${linkedMaster.name}` : "Pick a master record"}
                >
                    <option value="">— Pick master record —</option>
                    <optgroup label="Products">
                        {atomicMasters.filter((m) => m.kind === "product").map((m) => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                    </optgroup>
                    <optgroup label="Services">
                        {atomicMasters.filter((m) => m.kind === "service").map((m) => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                    </optgroup>
                </select>
                {linkedMaster && (
                    <div className="mt-0.5 text-[9px] text-slate-400 truncate" title={linkedMaster.code}>
                        {linkedMaster.kind} · <span className="font-mono">{linkedMaster.code}</span>
                    </div>
                )}
            </td>
            <td className="px-2 py-1.5">
                <Input
                    value={comp.component_code}
                    onChange={(e) => onChange({ component_code: e.target.value.replace(/\s+/g, "_") })}
                    placeholder="impl"
                    className="h-7 text-[11px] font-mono"
                    maxLength={32}
                    data-testid={`component-code-${idx}`}
                />
            </td>
            <td className="px-2 py-1.5">
                <Input
                    value={comp.label}
                    onChange={(e) => onChange({ label: e.target.value })}
                    placeholder="Core platform implementation"
                    className="h-7 text-[11px]"
                    maxLength={160}
                    data-testid={`component-label-${idx}`}
                />
            </td>
            <td className="px-2 py-1.5">
                <select
                    value={comp.fee_kind}
                    onChange={(e) => onChange({ fee_kind: e.target.value })}
                    className="h-7 w-full px-1.5 text-[11px] border border-slate-200 rounded bg-white"
                    data-testid={`component-fee-kind-${idx}`}
                >
                    {FEE_KIND_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </td>
            <td className="px-2 py-1.5">
                <Input
                    type="number"
                    min="0"
                    step="100"
                    value={comp.default_amount}
                    onChange={(e) => onChange({ default_amount: e.target.value })}
                    className="h-7 text-[11px] text-right font-mono"
                    data-testid={`component-amount-${idx}`}
                />
            </td>
            <td className="px-2 py-1.5">
                <select
                    value={comp.billing_frequency}
                    onChange={(e) => {
                        const nextFreq = e.target.value;
                        // If switching TO One-time and current rule is illegal,
                        // snap activation_rule back to "immediate".
                        const isIllegalForOneTime = nextFreq === "One-time"
                            && comp.activation_rule !== "immediate"
                            && comp.activation_rule !== "manual";
                        onChange({
                            billing_frequency: nextFreq,
                            ...(isIllegalForOneTime ? { activation_rule: "immediate", free_support_months: null } : {}),
                        });
                    }}
                    className="h-7 w-full px-1.5 text-[11px] border border-slate-200 rounded bg-white"
                    data-testid={`component-frequency-${idx}`}
                >
                    {BILLING_FREQ_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                    ))}
                </select>
            </td>
            <td className="px-2 py-1.5">
                <select
                    value={comp.activation_rule}
                    onChange={(e) => {
                        const nextRule = e.target.value;
                        onChange({
                            activation_rule: nextRule,
                            // Clear free_support_months whenever we move away from the
                            // free-support rule — server-side validator rejects stale values.
                            ...(nextRule !== "project_completion_plus_free_support"
                                ? { free_support_months: null }
                                : {}),
                        });
                    }}
                    className="h-7 w-full px-1.5 text-[11px] border border-slate-200 rounded bg-white"
                    data-testid={`component-rule-${idx}`}
                >
                    {ruleOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
                {showFreeMonths && (
                    <Input
                        type="number"
                        min="1"
                        max="120"
                        step="1"
                        value={comp.free_support_months ?? ""}
                        onChange={(e) => onChange({ free_support_months: Number(e.target.value) || null })}
                        placeholder="Free months"
                        className="h-7 mt-1 text-[11px] font-mono"
                        data-testid={`component-free-months-${idx}`}
                    />
                )}
            </td>
            <td className="px-2 py-1.5">
                {(() => {
                    const selected = Array.isArray(comp.delivery_channels) ? comp.delivery_channels : [];
                    const toggle = (v) => {
                        const next = selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v];
                        // Normalise empty → null so the read shape mirrors the backend default.
                        onChange({ delivery_channels: next.length ? next : null });
                    };
                    return (
                        <div className="flex gap-0.5 flex-wrap" data-testid={`component-channels-${idx}`}>
                            {DELIVERY_CHANNEL_OPTIONS.map((o) => {
                                const on = selected.includes(o.value);
                                return (
                                    <button
                                        key={o.value}
                                        type="button"
                                        onClick={() => toggle(o.value)}
                                        title={o.label}
                                        className={`px-1.5 h-5 rounded text-[10px] font-bold leading-none border ${on
                                            ? "bg-violet-600 text-white border-violet-600"
                                            : "bg-white text-slate-500 border-slate-200 hover:border-violet-400"}`}
                                        data-testid={`component-channel-${idx}-${o.value}`}
                                    >
                                        {o.short}
                                    </button>
                                );
                            })}
                        </div>
                    );
                })()}
            </td>
            <td className="px-2 py-1.5 w-7">
                <button
                    type="button"
                    onClick={onRemove}
                    className="text-rose-600 hover:text-rose-800 p-0.5"
                    title="Remove component"
                    data-testid={`component-remove-${idx}`}
                >
                    <Trash size={12} weight="bold" />
                </button>
            </td>
        </tr>
    );
}

function ItemDialog({ mode, item, onSaved, trigger, open, onOpenChange }) {
    const isEdit = mode === "edit";
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = typeof open === "boolean" && typeof onOpenChange === "function";
    const dialogOpen = isControlled ? open : internalOpen;
    const setDialogOpen = isControlled ? onOpenChange : setInternalOpen;

    const [name, setName] = useState("");
    const [code, setCode] = useState("");  // Iter 49.0 — natural key (auto-default if blank)
    const [kind, setKind] = useState("product");
    const [subtitle, setSubtitle] = useState("");
    const [softwareFee, setSoftwareFee] = useState(0);
    const [amcFee, setAmcFee] = useState(0);
    const [gstPercent, setGstPercent] = useState(18);
    const [hsnSacCode, setHsnSacCode] = useState("997331");
    // Iter C.0 — Phase C Commercial Packaging Model fields.
    const [packageKind, setPackageKind] = useState("atomic");
    const [components, setComponents] = useState([]);
    const [presentationMode, setPresentationMode] = useState("itemized");
    const [consolidatedLabel, setConsolidatedLabel] = useState("");
    const [hybridVisible, setHybridVisible] = useState([]);
    // Iter C.2.1.CAP — capability codes (free-text, soft taxonomy).
    const [capabilities, setCapabilities] = useState([]);
    // Iter 97.11 — Commercials Builder integration fields. Five additive
    // controls plus a simple newline-separated `code|label` editor for
    // builder_modules. All default to atomic-equivalent so the dialog
    // behaves identically to pre-97.11 for legacy rows.
    const [isActive, setIsActive] = useState(true);
    const [builderCategory, setBuilderCategory] = useState("");
    const [sortOrder, setSortOrder] = useState(100);
    const [mutuallyExclusive, setMutuallyExclusive] = useState(false);
    const [noDeliveryModes, setNoDeliveryModes] = useState(false);
    const [builderModulesText, setBuilderModulesText] = useState("");
    // Iter 97.17 — Structured Builder Modules list (replaces the
    // textarea); each entry is `{ code, label }`. We keep
    // `builderModulesText` in state ONLY for backward-compat dirty
    // tracking — `builderModules` is the source of truth for submission.
    const [builderModules, setBuilderModules] = useState([]);
    // Iter 97.17 — First-class delivery modes on the product master
    // (matches the 5-code DELIVERY_MODES vocabulary used by Builder
    // Section 1). Stored as `default_delivery_modes` on the backend.
    const [defaultDeliveryModes, setDefaultDeliveryModes] = useState([]);
    // Iter C.0.2 — atomic catalogue items, lazily loaded when dialog opens
    // so the component picker has the master-data list to choose from.
    const [atomicMasters, setAtomicMasters] = useState([]);

    useEffect(() => {
        if (!dialogOpen) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/billing/catalogue/list?limit=500");
                if (cancelled) return;
                const atomicOnly = (data.items || [])
                    .filter((it) => (it.package_kind || "atomic") === "atomic")
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
                setAtomicMasters(atomicOnly);
            } catch {
                // Non-blocking — picker simply shows no options if loading fails.
                setAtomicMasters([]);
            }
        })();
        return () => { cancelled = true; };
    }, [dialogOpen]);
    const [submitting, setSubmitting] = useState(false);
    // Lazy init baseline so the dialog isn't reported "dirty" on first render
    // (when useState defaults match the create-mode reset and useEffect's
    // setState calls become no-ops → no re-render to recompute isDirty).
    const baselineRef = useRef(JSON.stringify({
        name: "", code: "", kind: "product", subtitle: "",
        softwareFee: 0, amcFee: 0, gstPercent: 18, hsnSacCode: "997331",
        packageKind: "atomic", components: [], presentationMode: "itemized",
        consolidatedLabel: "", hybridVisible: [], capabilities: [],
        // Iter 97.11 — Builder integration fields.
        isActive: true, builderCategory: "", sortOrder: 100,
        mutuallyExclusive: false, noDeliveryModes: false, builderModulesText: "",
        // Iter 97.17 — Structured Builder Modules + first-class delivery modes.
        builderModules: [], defaultDeliveryModes: [],
    }));

    useEffect(() => {
        if (dialogOpen) {
            const nextName = isEdit && item ? item.name : "";
            const nextCode = isEdit && item ? (item.code || "") : "";
            const nextKind = isEdit && item ? item.kind : "product";
            const nextSub = isEdit && item ? (item.subtitle || "") : "";
            const nextSw = isEdit && item ? (item.default_software_fee || 0) : 0;
            const nextAmc = isEdit && item ? (item.default_amc_fee || 0) : 0;
            const nextGst = isEdit && item ? (item.default_gst_percent ?? 18) : 18;
            const nextHsn = isEdit && item ? (item.hsn_sac_code || "997331") : "997331";
            const nextPkg = isEdit && item ? (item.package_kind || "atomic") : "atomic";
            const nextComps = isEdit && item ? (item.components || []) : [];
            const nextPm = isEdit && item ? (item.default_presentation_mode || "itemized") : "itemized";
            const nextCl = isEdit && item ? (item.default_consolidated_label || "") : "";
            const nextHv = isEdit && item ? (item.default_hybrid_visible_components || []) : [];
            const nextCap = isEdit && item ? (item.capabilities || []) : [];
            // Iter 97.11 — Builder integration fields.
            const nextActive = isEdit && item ? item.is_active !== false : true;
            const nextCat = isEdit && item ? (item.category || "") : "";
            const nextSort = isEdit && item ? (item.sort_order ?? 100) : 100;
            const nextMutex = isEdit && item ? Boolean(item.mutually_exclusive) : false;
            const nextNoModes = isEdit && item ? Boolean(item.no_delivery_modes) : false;
            const nextBmText = isEdit && item
                ? (item.builder_modules || []).map((m) => `${m.code}|${m.label}`).join("\n")
                : "";
            // Iter 97.17 — Structured modules + first-class delivery modes.
            const nextBm = isEdit && item
                ? (item.builder_modules || []).map((m) => ({ code: m.code || "", label: m.label || "" }))
                : [];
            const nextDm = isEdit && item ? (item.default_delivery_modes || []) : [];
            setName(nextName); setCode(nextCode); setKind(nextKind); setSubtitle(nextSub);
            setSoftwareFee(nextSw); setAmcFee(nextAmc); setGstPercent(nextGst);
            setHsnSacCode(nextHsn);
            setPackageKind(nextPkg); setComponents(nextComps); setPresentationMode(nextPm);
            setConsolidatedLabel(nextCl); setHybridVisible(nextHv);
            setCapabilities(nextCap);
            setIsActive(nextActive); setBuilderCategory(nextCat); setSortOrder(nextSort);
            setMutuallyExclusive(nextMutex); setNoDeliveryModes(nextNoModes);
            setBuilderModulesText(nextBmText);
            setBuilderModules(nextBm); setDefaultDeliveryModes(nextDm);
            baselineRef.current = JSON.stringify({
                name: nextName, code: nextCode, kind: nextKind, subtitle: nextSub,
                softwareFee: nextSw, amcFee: nextAmc, gstPercent: nextGst, hsnSacCode: nextHsn,
                packageKind: nextPkg, components: nextComps, presentationMode: nextPm,
                consolidatedLabel: nextCl, hybridVisible: nextHv, capabilities: nextCap,
                isActive: nextActive, builderCategory: nextCat, sortOrder: nextSort,
                mutuallyExclusive: nextMutex, noDeliveryModes: nextNoModes,
                builderModulesText: nextBmText,
                builderModules: nextBm, defaultDeliveryModes: nextDm,
            });
        }
    }, [dialogOpen, isEdit, item]);

    const isDirty = JSON.stringify({
        name, code, kind, subtitle, softwareFee, amcFee, gstPercent, hsnSacCode,
        packageKind, components, presentationMode, consolidatedLabel, hybridVisible,
        capabilities,
        isActive, builderCategory, sortOrder, mutuallyExclusive, noDeliveryModes,
        builderModulesText,
        builderModules, defaultDeliveryModes,
    }) !== baselineRef.current;

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        const payload = {
            name: name.trim(),
            // Iter 49.0 — natural key. Send only on create (immutable on edit).
            // Backend will auto-default to slug(name) when blank on create.
            ...(isEdit ? {} : { code: code.trim() }),
            kind,
            subtitle: subtitle.trim(),
            default_software_fee: Number(softwareFee) || 0,
            default_amc_fee: Number(amcFee) || 0,
            default_gst_percent: Number(gstPercent) || 0,
            hsn_sac_code: (hsnSacCode || "").trim() || "997331",
            // Iter C.0 — Phase C Commercial Packaging fields.
            package_kind: packageKind,
            components: packageKind === "package"
                ? components.map((c) => ({
                    source_catalogue_id: (c.source_catalogue_id || "").trim(),
                    component_code: (c.component_code || "").trim(),
                    label: (c.label || "").trim(),
                    fee_kind: c.fee_kind,
                    default_amount: Number(c.default_amount) || 0,
                    billing_frequency: c.billing_frequency,
                    activation_rule: c.activation_rule,
                    free_support_months: c.activation_rule === "project_completion_plus_free_support"
                        ? (Number(c.free_support_months) || null)
                        : null,
                    gst_percent_override: c.gst_percent_override ?? null,
                    hsn_sac_code_override: c.hsn_sac_code_override ?? null,
                    is_visible_in_print_default: c.is_visible_in_print_default !== false,
                    description: c.description ?? null,
                    // Iter C.1.2c — multi-valued delivery surfaces (normalise
                    // empty to null so the read shape stays unambiguous).
                    delivery_channels: Array.isArray(c.delivery_channels) && c.delivery_channels.length
                        ? c.delivery_channels
                        : null,
                }))
                : [],
            default_presentation_mode: presentationMode,
            default_consolidated_label: presentationMode === "consolidated" ? consolidatedLabel.trim() : null,
            default_hybrid_visible_components: presentationMode === "hybrid" ? hybridVisible : [],
            // Iter C.2.1.CAP — capability codes.
            capabilities: capabilities,
            // Iter 97.11 — Commercials Builder integration fields.
            is_active: Boolean(isActive),
            category: (builderCategory || "").trim(),
            sort_order: Number(sortOrder) || 100,
            mutually_exclusive: Boolean(mutuallyExclusive),
            no_delivery_modes: Boolean(noDeliveryModes),
            // Iter 97.17 — Prefer the structured list editor; fall back to
            // parsing the legacy textarea so edits made via the old UI
            // surface still apply if `builderModules` is empty.
            builder_modules: (builderModules && builderModules.length > 0)
                ? builderModules
                    .map((m) => ({
                        code: (m.code || "").trim(),
                        label: ((m.label || "").trim() || (m.code || "").trim()),
                    }))
                    .filter((m) => m.code)
                : (builderModulesText || "")
                    .split("\n")
                    .map((line) => {
                        const trimmed = line.trim();
                        if (!trimmed) return null;
                        const [c, ...rest] = trimmed.split("|");
                        const code = (c || "").trim();
                        const label = rest.join("|").trim() || code;
                        if (!code) return null;
                        return { code, label };
                    })
                    .filter(Boolean),
            // Iter 97.17 — First-class delivery modes (5-code vocabulary).
            default_delivery_modes: Array.isArray(defaultDeliveryModes) ? defaultDeliveryModes : [],
        };
        setSubmitting(true);
        try {
            if (isEdit) {
                await api.patch(`/billing/catalogue/${item.id}`, payload);
                toast.success(`Updated "${payload.name}"`);
            } else {
                await api.post("/billing/catalogue", payload);
                toast.success(`Added "${payload.name}" to catalogue`);
            }
            baselineRef.current = JSON.stringify({
                name, code, kind, subtitle, softwareFee, amcFee, gstPercent, hsnSacCode,
                packageKind, components, presentationMode, consolidatedLabel, hybridVisible,
                capabilities,
            });  // bypass discard prompt
            setDialogOpen(false);
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err, isEdit ? "Failed to update" : "Failed to create"));
        } finally {
            setSubmitting(false);
        }
    };

    // Iter C.0 — Component editor helpers
    const addComponent = () => {
        setComponents([...components, EMPTY_COMPONENT()]);
    };
    const updateComponent = (idx, patch) => {
        setComponents(components.map((c, i) => i === idx ? { ...c, ...patch } : c));
    };
    const removeComponent = (idx) => {
        const removed = components[idx]?.component_code;
        setComponents(components.filter((_, i) => i !== idx));
        // Keep hybrid_visible coherent if we just removed a visible component.
        if (removed && hybridVisible.includes(removed)) {
            setHybridVisible(hybridVisible.filter((c) => c !== removed));
        }
    };

    // Iter C.0 — Presentation preview rows. Derives what a proposal line
    // would look like for each of the 3 modes from the current dialog
    // state. This is purely a visualisation aid for catalogue admins —
    // it does NOT touch the proposal model (which lands in C.1).
    const previewRows = useMemo(() => {
        const valid = components.filter(
            (c) => (c.component_code || "").trim() && (c.label || "").trim(),
        );
        if (presentationMode === "itemized") {
            return valid.map((c) => ({
                kind: "single",
                label: c.label,
                amount: Number(c.default_amount) || 0,
                frequency: c.billing_frequency,
                fee_kind: c.fee_kind,
            }));
        }
        if (presentationMode === "consolidated") {
            const sumOneTime = valid
                .filter((c) => c.billing_frequency === "One-time")
                .reduce((s, c) => s + Number(c.default_amount || 0), 0);
            const sumRecurring = valid
                .filter((c) => c.billing_frequency !== "One-time")
                .reduce((s, c) => s + Number(c.default_amount || 0), 0);
            const total = sumOneTime + sumRecurring;
            return [{
                kind: "single",
                label: consolidatedLabel || "(Set a consolidated line label above)",
                amount: total,
                frequency: "Year-1 bundle",
                fee_kind: null,
                isPlaceholder: !consolidatedLabel,
            }];
        }
        // hybrid
        const rows = [];
        const visible = new Set(hybridVisible);
        valid.forEach((c) => {
            if (visible.has(c.component_code)) {
                rows.push({
                    kind: "single",
                    label: c.label,
                    amount: Number(c.default_amount) || 0,
                    frequency: c.billing_frequency,
                    fee_kind: c.fee_kind,
                });
            }
        });
        const rolledUp = valid.filter((c) => !visible.has(c.component_code));
        if (rolledUp.length > 0) {
            const total = rolledUp.reduce((s, c) => s + Number(c.default_amount || 0), 0);
            rows.push({
                kind: "rollup",
                label: `Bundled services (${rolledUp.length} component${rolledUp.length === 1 ? "" : "s"})`,
                amount: total,
                frequency: rolledUp.every((c) => c.billing_frequency === "One-time") ? "One-time" : "Mixed",
                fee_kind: null,
                rolledComponents: rolledUp.map((c) => c.label),
            });
        }
        return rows;
    }, [components, presentationMode, consolidatedLabel, hybridVisible]);

    const previewTotal = previewRows.reduce((s, r) => s + (r.amount || 0), 0);

    return (
        <>
            {trigger && cloneElement(trigger, { onClick: () => setDialogOpen(true) })}
            <FormDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                isDirty={isDirty}
                contentClassName={packageKind === "package" ? "max-w-4xl" : "max-w-xl"}
                testId="catalogue-dialog"
                title={isEdit ? "Edit catalogue item" : "Add catalogue item"}
                description="Items here become the picker options when adding line items to a Proposal or PO. Fees are defaults — they can still be overridden on each proposal."
            >
                <form onSubmit={submit} className="space-y-4" data-testid="catalogue-form">
                    {/* Iter 40.1 — soft, non-blocking dependency notice.
                        Shown only on Edit when there's actual downstream usage.
                        Tone is intentionally informational (slate/sky), NOT a
                        warning state — the snapshot model means edits are
                        always safe; we're just informing about scope.   */}
                    {isEdit && Number(item?.usage?.templates || 0) > 0 && (
                        <div
                            className="rounded-md border border-sky-100 bg-sky-50/60 px-3 py-2.5 text-[11px] leading-relaxed text-slate-700 flex items-start gap-2"
                            data-testid="catalogue-usage-notice"
                        >
                            <Stack size={12} weight="duotone" className="text-sky-700 mt-0.5 shrink-0" />
                            <div>
                                <span className="text-slate-900 font-medium">
                                    This catalogue item is currently referenced by {item.usage.templates} active template{item.usage.templates === 1 ? "" : "s"}.
                                </span>
                                <span className="text-slate-600">
                                    {" "}Changes will affect future usage only. Existing template, proposal, and invoice snapshots remain immutable.
                                </span>
                            </div>
                        </div>
                    )}
                    <div>
                        <Label className="text-xs font-semibold">Name</Label>
                        <Input
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder='e.g. "BSE Star MFD — Browser (white-labelled)"'
                            className="mt-1.5"
                            data-testid="catalogue-name-input"
                        />
                        <p className="mt-1 text-[10px] text-slate-500">
                            "Others" is reserved — it appears automatically as the last picker option for free-form entries.
                        </p>
                    </div>
                    {/* Iter 49.0 — Natural-key code field. Editable on create
                        (auto-defaults to slug(name) when blank), read-only on
                        edit (natural keys are immutable in Iter 49.0). */}
                    <div>
                        <Label className="text-xs font-semibold">Catalogue code</Label>
                        <Input
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder={isEdit ? "" : 'auto-derived from name if left blank, e.g. "bse-star-mfd-browser"'}
                            className="mt-1.5 font-mono text-xs"
                            maxLength={64}
                            readOnly={isEdit}
                            disabled={isEdit}
                            data-testid="catalogue-code-input"
                        />
                        <p className="mt-1 text-[10px] text-slate-500">
                            {isEdit
                                ? "Natural key — set on create, immutable thereafter."
                                : "Unique slug within this kind. Used by CSV import & external system references."}
                        </p>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Subtitle <span className="font-normal text-slate-400">(optional · second line on the proposal)</span></Label>
                        <Input
                            value={subtitle}
                            onChange={(e) => setSubtitle(e.target.value)}
                            placeholder='e.g. "One-time · Annual"  or  "Optional"'
                            className="mt-1.5"
                            data-testid="catalogue-subtitle-input"
                            maxLength={120}
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Kind</Label>
                        <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid="catalogue-kind-toggle">
                            {(["product", "service"]).map((k) => {
                                const M = KIND_META[k];
                                const active = kind === k;
                                return (
                                    <button
                                        key={k}
                                        type="button"
                                        onClick={() => setKind(k)}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-semibold transition-colors ${
                                            active
                                                ? `${M.tone} border-current`
                                                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                                        }`}
                                        data-testid={`catalogue-kind-${k}`}
                                    >
                                        <M.icon size={14} weight="fill" />
                                        {M.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3" data-testid="catalogue-fees">
                        <div>
                            <Label className={`text-xs font-semibold ${packageKind === "package" ? "text-slate-400" : ""}`}>Default Software & Set-Up Fee (₹)</Label>
                            <Input
                                type="number"
                                min="0"
                                step="100"
                                value={softwareFee}
                                onChange={(e) => setSoftwareFee(e.target.value)}
                                disabled={packageKind === "package"}
                                className="mt-1.5 font-mono"
                                data-testid="catalogue-sw-fee-input"
                            />
                        </div>
                        <div>
                            <Label className={`text-xs font-semibold ${packageKind === "package" ? "text-slate-400" : ""}`}>Default AMC Fee (₹)</Label>
                            <Input
                                type="number"
                                min="0"
                                step="100"
                                value={amcFee}
                                onChange={(e) => setAmcFee(e.target.value)}
                                disabled={packageKind === "package"}
                                className="mt-1.5 font-mono"
                                data-testid="catalogue-amc-fee-input"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">GST %</Label>
                            <select
                                value={gstPercent}
                                onChange={(e) => setGstPercent(e.target.value)}
                                className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white"
                                data-testid="catalogue-gst-select"
                            >
                                {[0, 5, 12, 18, 28].map((g) => (
                                    <option key={g} value={g}>{g}%</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {packageKind === "package" && (
                        <p className="text-[10px] text-slate-500 -mt-2" data-testid="catalogue-package-fee-hint">
                            Software & AMC defaults are ignored for packages — define amounts per component below instead.
                        </p>
                    )}

                    {/* Iter 38: HSN/SAC code — auto-fills downstream line items */}
                    <div>
                        <Label className="text-xs font-semibold">
                            HSN / SAC code
                            <span className="font-normal text-slate-400 ml-1">(default 997331 · auto-populates on proposals, POs, AMCs & invoices)</span>
                        </Label>
                        <Input
                            value={hsnSacCode}
                            onChange={(e) => setHsnSacCode(e.target.value.replace(/\s+/g, ""))}
                            placeholder="997331"
                            maxLength={20}
                            className="mt-1.5 font-mono"
                            data-testid="catalogue-hsn-input"
                        />
                        <p className="mt-1 text-[10px] text-slate-500">
                            Snapshotted at line-item level — historical proposals & invoices stay immutable even if you change this later.
                        </p>
                    </div>

                    {/* Iter C.2.1.CAP — Capability badges authoring (Presentation, soft taxonomy).
                        Pure visual scope-communication metadata. Snapshots onto
                        proposal/PO lines at add-time per the K-B storage model. */}
                    <div className="border-t border-slate-200 pt-4">
                        <CapabilityPicker value={capabilities} onChange={setCapabilities} />
                    </div>

                    {/* Iter 97.11 — Commercials Builder integration block.
                        Controls how this catalogue item appears inside the
                        proposal-scoped Commercials Builder picker. Hidden
                        for services (kind=service) since the Builder only
                        surfaces Products in Section 1. */}
                    {kind === "product" && (
                        <div className="border-t border-slate-200 pt-4 space-y-3" data-testid="catalogue-builder-block">
                            <div className="flex items-center justify-between">
                                <Label className="text-xs font-semibold tracking-wide text-slate-700">
                                    Commercials Builder visibility
                                </Label>
                                <button
                                    type="button"
                                    onClick={() => setIsActive((v) => !v)}
                                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                                        isActive
                                            ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                                            : "bg-slate-100 text-slate-500 border-slate-300"
                                    }`}
                                    data-testid="catalogue-is-active-toggle"
                                >
                                    {isActive ? "Active in Builder" : "Archived"}
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-[11px] font-semibold">Business category</Label>
                                    <Input
                                        value={builderCategory}
                                        onChange={(e) => setBuilderCategory(e.target.value)}
                                        placeholder="e.g., aiM_Wealth (blank = top-level)"
                                        maxLength={80}
                                        className="mt-1 text-sm"
                                        data-testid="catalogue-builder-category-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-[11px] font-semibold">Sort order</Label>
                                    <Input
                                        type="number"
                                        value={sortOrder}
                                        onChange={(e) => setSortOrder(Number(e.target.value))}
                                        min={0}
                                        max={9999}
                                        className="mt-1 text-sm font-mono"
                                        data-testid="catalogue-builder-sort-order-input"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <label className="flex items-start gap-2 text-[12px] text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={mutuallyExclusive}
                                        onChange={(e) => setMutuallyExclusive(e.target.checked)}
                                        className="mt-0.5"
                                        data-testid="catalogue-mutex-checkbox"
                                    />
                                    <span>
                                        <span className="font-semibold">Mutually exclusive modules</span>
                                        <span className="block text-[10px] text-slate-500">e.g., Hosting (REEDOS vs On-Premises)</span>
                                    </span>
                                </label>
                                <label className="flex items-start gap-2 text-[12px] text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={noDeliveryModes}
                                        onChange={(e) => setNoDeliveryModes(e.target.checked)}
                                        className="mt-0.5"
                                        data-testid="catalogue-no-delivery-modes-checkbox"
                                    />
                                    <span>
                                        <span className="font-semibold">Hide delivery-mode icons</span>
                                        <span className="block text-[10px] text-slate-500">For Products where delivery is implicit</span>
                                    </span>
                                </label>
                            </div>
                            <div>
                                <Label className="text-[11px] font-semibold">
                                    Builder Modules
                                    <span className="font-normal text-slate-500 ml-1">
                                        — scope rows shown under this Product in Builder Section 1
                                    </span>
                                </Label>
                                <div className="mt-1.5 space-y-1.5" data-testid="catalogue-builder-modules-list">
                                    {builderModules.length === 0 && (
                                        <div className="text-[10px] text-slate-400 italic py-1">
                                            No modules. Builder will render a single checkbox for this Product.
                                        </div>
                                    )}
                                    {builderModules.map((m, i) => (
                                        <div key={i} className="flex items-center gap-1.5" data-testid={`catalogue-builder-module-row-${i}`}>
                                            <Input
                                                value={m.code}
                                                onChange={(e) => {
                                                    const v = e.target.value.replace(/\s+/g, "_").toLowerCase();
                                                    setBuilderModules((prev) => prev.map((x, idx) => idx === i ? { ...x, code: v } : x));
                                                }}
                                                placeholder="aw-listed-equity-bse"
                                                className="h-7 w-44 text-[11px] font-mono"
                                                maxLength={64}
                                                data-testid={`catalogue-builder-module-code-${i}`}
                                            />
                                            <Input
                                                value={m.label}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setBuilderModules((prev) => prev.map((x, idx) => idx === i ? { ...x, label: v } : x));
                                                }}
                                                placeholder="Listed Equity – BSE"
                                                className="h-7 flex-1 text-[11px]"
                                                maxLength={160}
                                                data-testid={`catalogue-builder-module-label-${i}`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setBuilderModules((prev) => prev.filter((_, idx) => idx !== i))}
                                                className="h-7 w-7 inline-flex items-center justify-center rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                                                aria-label="Remove module"
                                                data-testid={`catalogue-builder-module-remove-${i}`}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setBuilderModules((prev) => [...prev, { code: "", label: "" }])}
                                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 hover:text-violet-900"
                                    data-testid="catalogue-builder-module-add-btn"
                                >
                                    + Add Module
                                </button>
                                <p className="mt-1 text-[10px] text-slate-500">
                                    These render as the asset-class scope rows in Builder Section 1 (e.g., aiM-Wealth → Listed Equity BSE/NSE).
                                </p>
                            </div>

                            {/* Iter 97.17 — First-class Delivery Modes picker.
                                The 5 codes (browser/android/ios/api/data_feed)
                                mirror the Builder Section 1 chip strip exactly,
                                so the Builder picker shows the same icons the
                                operator selected here. Saved to the backend as
                                `default_delivery_modes`. */}
                            <div>
                                <Label className="text-[11px] font-semibold">
                                    Delivery Modes
                                    <span className="font-normal text-slate-500 ml-1">
                                        — product-level metadata (shown as icons in Builder + Proposal Detail + PDF)
                                    </span>
                                </Label>
                                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="catalogue-delivery-modes-picker">
                                    {[
                                        { code: "browser", label: "Browser / Web" },
                                        { code: "android", label: "Android" },
                                        { code: "ios", label: "iOS" },
                                        { code: "api", label: "API" },
                                        { code: "data_feed", label: "Data Feed" },
                                    ].map((dm) => {
                                        const selected = defaultDeliveryModes.includes(dm.code);
                                        return (
                                            <button
                                                key={dm.code}
                                                type="button"
                                                disabled={noDeliveryModes}
                                                onClick={() => {
                                                    setDefaultDeliveryModes((prev) =>
                                                        prev.includes(dm.code)
                                                            ? prev.filter((c) => c !== dm.code)
                                                            : [...prev, dm.code],
                                                    );
                                                }}
                                                className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${
                                                    noDeliveryModes
                                                        ? "bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed"
                                                        : selected
                                                            ? "bg-violet-600 text-white border-violet-600"
                                                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                                                }`}
                                                data-testid={`catalogue-delivery-mode-${dm.code}`}
                                            >
                                                {dm.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="mt-1 text-[10px] text-slate-500">
                                    {noDeliveryModes
                                        ? "Disabled because \"Hide delivery-mode icons\" is checked above."
                                        : "Pick the delivery surfaces this Product ships on. Used by Builder Section 1 + the icon strip on Proposal Detail / PDF."}
                                </p>
                            </div>

                            {/* Legacy textarea — kept hidden so prior dirty
                                tracking + backfill from old data still works.
                                Operators only see the structured list above. */}
                            <input type="hidden" value={builderModulesText} readOnly data-testid="catalogue-builder-modules-textarea-hidden" />
                        </div>
                    )}

                    {/* Iter C.0 — Phase C Commercial Packaging Model.
                        Toggle between atomic (legacy single-fee) and package
                        (multi-component bundle). Atomic items behave exactly
                        as before; the entire package block stays unmounted
                        until the operator opts in. */}
                    <div className="border-t border-slate-200 pt-4" data-testid="catalogue-package-block">
                        <div>
                            <Label className="text-xs font-semibold">Catalogue mode</Label>
                            <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid="catalogue-package-kind-toggle">
                                {[
                                    { key: "atomic", label: "Atomic", icon: Cube, hint: "Single name, single fee block" },
                                    { key: "package", label: "Package", icon: StackIcon, hint: "Bundle of internal components" },
                                ].map((opt) => {
                                    const active = packageKind === opt.key;
                                    const Icon = opt.icon;
                                    return (
                                        <button
                                            key={opt.key}
                                            type="button"
                                            onClick={() => setPackageKind(opt.key)}
                                            className={`text-left px-3 py-2.5 rounded-md border transition-colors ${
                                                active
                                                    ? "bg-violet-50 text-violet-800 border-violet-300"
                                                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                                            }`}
                                            data-testid={`catalogue-package-kind-${opt.key}`}
                                        >
                                            <div className="flex items-center gap-2 font-semibold text-sm">
                                                <Icon size={14} weight={active ? "fill" : "duotone"} />
                                                {opt.label}
                                            </div>
                                            <div className="text-[10px] text-slate-500 mt-0.5">{opt.hint}</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {packageKind === "package" && (
                            <div className="mt-4 space-y-4" data-testid="catalogue-package-editor">
                                {/* Components table */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <Label className="text-xs font-semibold">Internal components</Label>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={addComponent}
                                            className="h-7 gap-1 text-[11px]"
                                            data-testid="catalogue-add-component-btn"
                                        >
                                            <Plus size={11} weight="bold" /> Add component
                                        </Button>
                                    </div>
                                    {components.length === 0 ? (
                                        <div className="rounded-md border border-dashed border-slate-300 px-3 py-6 text-center text-[11px] text-slate-500" data-testid="catalogue-components-empty">
                                            No components yet — add at least one to author this as a package.
                                        </div>
                                    ) : (
                                        <div className="rounded-md border border-slate-200 overflow-hidden" data-testid="catalogue-components-table">
                                            <table className="w-full text-xs">
                                                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 border-b border-slate-200">
                                                    <tr>
                                                        <th className="px-2 py-1.5 text-left font-semibold">Source (master record)</th>
                                                        <th className="px-2 py-1.5 text-left font-semibold">Code</th>
                                                        <th className="px-2 py-1.5 text-left font-semibold">Label</th>
                                                        <th className="px-2 py-1.5 text-left font-semibold">Kind</th>
                                                        <th className="px-2 py-1.5 text-right font-semibold">Default ₹</th>
                                                        <th className="px-2 py-1.5 text-left font-semibold">Frequency</th>
                                                        <th className="px-2 py-1.5 text-left font-semibold">Activation</th>
                                                        <th className="px-2 py-1.5 text-left font-semibold" title="Delivery surfaces this component ships on">Channels</th>
                                                        <th className="px-2 py-1.5"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {components.map((c, idx) => (
                                                        <ComponentRow
                                                            key={idx}
                                                            idx={idx}
                                                            comp={c}
                                                            onChange={(patch) => updateComponent(idx, patch)}
                                                            onRemove={() => removeComponent(idx)}
                                                            atomicMasters={atomicMasters}
                                                            allCodes={components.map((cc) => cc.component_code || "")}
                                                        />
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                    <p className="mt-1.5 text-[10px] text-slate-500">
                                        Each component is its own commercial signal. Recurring components carry an Activation Rule
                                        (e.g. AMC starts after a free-support window) — the system proposes drafts at the right
                                        time; the operator decides when to activate.
                                    </p>
                                </div>

                                {/* Presentation mode */}
                                <div>
                                    <Label className="text-xs font-semibold">Default presentation on proposals/POs</Label>
                                    <div className="mt-1.5 grid grid-cols-3 gap-2" data-testid="catalogue-presentation-mode">
                                        {PRESENTATION_MODE_OPTIONS.map((opt) => {
                                            const active = presentationMode === opt.value;
                                            return (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    onClick={() => setPresentationMode(opt.value)}
                                                    className={`text-left px-3 py-2 rounded-md border transition-colors ${
                                                        active
                                                            ? "bg-indigo-50 text-indigo-800 border-indigo-300"
                                                            : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                                                    }`}
                                                    data-testid={`catalogue-presentation-${opt.value}`}
                                                >
                                                    <div className="font-semibold text-xs">{opt.label}</div>
                                                    <div className="text-[10px] text-slate-500 mt-0.5">{opt.hint}</div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="mt-1.5 text-[10px] text-slate-500">
                                        Suggested default — proposal authors can override per client.
                                    </p>
                                </div>

                                {presentationMode === "consolidated" && (
                                    <div data-testid="catalogue-consolidated-label-block">
                                        <Label className="text-xs font-semibold">Consolidated line label</Label>
                                        <Input
                                            value={consolidatedLabel}
                                            onChange={(e) => setConsolidatedLabel(e.target.value)}
                                            placeholder='e.g. "aiM-Fissis Year-1 Setup + First-Year Services"'
                                            className="mt-1.5"
                                            maxLength={200}
                                            data-testid="catalogue-consolidated-label-input"
                                        />
                                    </div>
                                )}

                                {presentationMode === "hybrid" && components.length > 0 && (
                                    <div data-testid="catalogue-hybrid-visible-block">
                                        <Label className="text-xs font-semibold">Visible components in hybrid mode</Label>
                                        <div className="mt-2 grid grid-cols-2 gap-1.5">
                                            {components.map((c) => {
                                                if (!c.component_code) return null;
                                                const checked = hybridVisible.includes(c.component_code);
                                                return (
                                                    <label
                                                        key={c.component_code}
                                                        className="flex items-center gap-2 px-2.5 py-1.5 rounded border border-slate-200 hover:bg-slate-50 cursor-pointer text-xs"
                                                        data-testid={`catalogue-hybrid-visible-${c.component_code}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setHybridVisible([...hybridVisible, c.component_code]);
                                                                } else {
                                                                    setHybridVisible(hybridVisible.filter((x) => x !== c.component_code));
                                                                }
                                                            }}
                                                        />
                                                        <span className="font-mono text-[10px] text-slate-500">{c.component_code}</span>
                                                        <span className="truncate text-slate-700">{c.label || "(unnamed)"}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        <p className="mt-1.5 text-[10px] text-slate-500">
                                            Checked components shown as separate rows; unchecked rolled into a single bundled line.
                                        </p>
                                    </div>
                                )}

                                {/* Iter C.0 — Live presentation preview. Renders a mini
                                    "as it would appear on a proposal line" mock that
                                    reacts to changes in components, presentation mode,
                                    consolidated label, and hybrid visibility. Purely
                                    illustrative — no proposal involved. */}
                                {previewRows.length > 0 && (
                                    <div className="rounded-md border border-indigo-200 bg-indigo-50/40 p-3" data-testid="catalogue-presentation-preview">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="text-[10px] font-semibold text-indigo-700 uppercase tracking-wide">
                                                How this will appear on a proposal line · {presentationMode}
                                            </div>
                                            <div className="text-[10px] text-indigo-500 italic">Live preview</div>
                                        </div>
                                        <div className="rounded border border-white bg-white overflow-hidden">
                                            <table className="w-full text-xs">
                                                <thead className="bg-slate-50 border-b border-slate-200 text-[9px] uppercase text-slate-500">
                                                    <tr>
                                                        <th className="px-2 py-1.5 text-left font-semibold">Client-visible line</th>
                                                        <th className="px-2 py-1.5 text-left font-semibold">Cadence</th>
                                                        <th className="px-2 py-1.5 text-right font-semibold">Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {previewRows.map((r, i) => (
                                                        <tr
                                                            key={i}
                                                            className={`border-b border-slate-100 last:border-b-0 ${r.kind === "rollup" ? "bg-amber-50/40" : ""}`}
                                                            data-testid={`preview-row-${i}`}
                                                        >
                                                            <td className="px-2 py-1.5">
                                                                <div className={`font-medium ${r.isPlaceholder ? "italic text-slate-400" : "text-slate-800"}`}>
                                                                    {r.label}
                                                                </div>
                                                                {r.kind === "rollup" && (
                                                                    <div className="text-[10px] text-amber-700 mt-0.5">
                                                                        Internally: {r.rolledComponents.join(" · ")}
                                                                    </div>
                                                                )}
                                                                {r.fee_kind && (
                                                                    <div className="text-[10px] text-slate-400 mt-0.5 capitalize">{r.fee_kind}</div>
                                                                )}
                                                            </td>
                                                            <td className="px-2 py-1.5 text-slate-600">{r.frequency}</td>
                                                            <td className="px-2 py-1.5 text-right font-mono font-semibold text-slate-800">
                                                                ₹{Number(r.amount).toLocaleString("en-IN")}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {previewRows.length > 1 && (
                                                        <tr className="bg-slate-50 border-t-2 border-slate-200">
                                                            <td colSpan={2} className="px-2 py-1.5 text-right font-semibold text-slate-700 text-[10px] uppercase">
                                                                Year-1 total (pre-GST)
                                                            </td>
                                                            <td className="px-2 py-1.5 text-right font-mono font-bold text-slate-900">
                                                                ₹{Number(previewTotal).toLocaleString("en-IN")}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        <p className="mt-2 text-[10px] text-indigo-700/80 leading-snug">
                                            Internal structure (all {components.filter((c) => c.component_code && c.label).length} components) is always preserved in the snapshot —
                                            regardless of presentation mode — so AMC drafts, project ledger and audit trails see the full breakdown.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <FormDialogCancel testId="catalogue-cancel-btn">Cancel</FormDialogCancel>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
                            data-testid="catalogue-submit-btn"
                        >
                            <FloppyDisk size={14} weight="fill" />
                            {submitting ? "Saving…" : isEdit ? "Save changes" : "Add to catalogue"}
                        </Button>
                    </DialogFooter>
                </form>
            </FormDialog>
        </>
    );
}

export default function Catalogue() {
    const { can } = useAuth();
    // Iter 66.4 — Sales has read-only access (no `template:write`). The page
    // renders fully, but Create / Edit / Delete affordances are gated below.
    const canWrite = can("template:write");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editTarget, setEditTarget] = useState(null);
    // Iter 97.17 — Default-hide archived items so the operator sees the live
    // Builder picker by default. The "Show archived" pill expands the list
    // to include them again.
    const [showArchived, setShowArchived] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/billing/catalogue/list", { params: { include_usage: true } });
            setItems(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load catalogue"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const groups = useMemo(() => {
        // Iter 97.17 — Products: split into active vs archived so the
        // Builder-driven default view (active only) is the primary surface.
        // Archived Products + Services with is_active=false stay hidden
        // until the "Show archived" toggle is on.
        const products = items.filter((i) => i.kind === "product");
        const services = items.filter((i) => i.kind === "service");
        const productsActive = products.filter((p) => p.is_active !== false);
        const productsArchived = products.filter((p) => p.is_active === false);
        return { products, productsActive, productsArchived, services };
    }, [items]);

    const remove = async (it) => {
        if (!window.confirm(`Remove "${it.name}" from the catalogue? This won't affect existing proposals or invoices.`)) return;
        try {
            await api.delete(`/billing/catalogue/${it.id}`);
            toast.success(`Removed "${it.name}"`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to delete"));
        }
    };

    return (
        <div data-testid="catalogue-page">
            <PageHeader
                eyebrow={canWrite ? "Master data" : "Master data · Read-only"}
                title="Product Master · Proposal Builder"
                description={
                    canWrite
                        ? "Products + Modules + Delivery Modes maintained here drive the picker in Builder Section 1. Edits here apply to every future proposal."
                        : "Browse the Product Master that drives the Builder. Editing is reserved for admin governance — speak to a Super Admin if you need an item added."
                }
                actions={canWrite ? (
                    <ItemDialog
                        mode="create"
                        onSaved={load}
                        trigger={
                            <Button data-testid="catalogue-add-btn" className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5">
                                <Plus size={14} weight="bold" /> Add Product
                            </Button>
                        }
                    />
                ) : null}
            />

            <div className="px-6 sm:px-8 py-8 space-y-8">
                {loading ? (
                    <div className="text-sm text-slate-500">Loading catalogue…</div>
                ) : (
                    <>
                        {/* Iter 97.17 — Show archived toggle. Default view
                            shows only Active Builder Products so archived
                            test fixtures (e.g. legacy CAP-* regression rows)
                            don't pollute the list. */}
                        {(groups.productsArchived.length > 0 || (groups.services.length > 0 && groups.services.some((s) => s.is_active === false))) && (
                            <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5" data-testid="catalogue-archived-toggle-row">
                                <div className="text-[12px] text-slate-600">
                                    <span className="font-semibold text-slate-700">{groups.productsArchived.length}</span> archived product{groups.productsArchived.length !== 1 ? "s" : ""} hidden from the Builder picker.
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowArchived((v) => !v)}
                                    className={`text-[11px] font-semibold px-3 py-1 rounded-full border transition-colors ${
                                        showArchived
                                            ? "bg-violet-600 text-white border-violet-600"
                                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                                    }`}
                                    data-testid="catalogue-show-archived-toggle"
                                >
                                    {showArchived ? "Hide archived" : `Show archived (${groups.productsArchived.length})`}
                                </button>
                            </div>
                        )}
                        <CatalogueSection
                            title="Products"
                            kind="product"
                            items={showArchived ? groups.products : groups.productsActive}
                            onEdit={setEditTarget}
                            onRemove={remove}
                            canWrite={canWrite}
                        />
                        <CatalogueSection
                            title="Services"
                            kind="service"
                            items={groups.services}
                            onEdit={setEditTarget}
                            onRemove={remove}
                            canWrite={canWrite}
                        />
                    </>
                )}
            </div>

            {canWrite && (
            <ItemDialog
                mode="edit"
                item={editTarget}
                open={Boolean(editTarget)}
                onOpenChange={(o) => { if (!o) setEditTarget(null); }}
                onSaved={load}
            />
            )}
        </div>
    );
}

function CatalogueSection({ title, kind, items, onEdit, onRemove, canWrite }) {
    const M = KIND_META[kind];
    return (
        <section className="reedos-card overflow-hidden" data-testid={`catalogue-section-${kind}`}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[10px] font-semibold border uppercase tracking-wider ${M.tone}`}>
                        <M.icon size={11} weight="fill" /> {title}
                    </span>
                    <span className="text-xs text-slate-500">{items.length} item{items.length !== 1 ? "s" : ""}</span>
                </div>
            </div>
            {items.length === 0 ? (
                <div className="px-5 py-10 text-center text-sm text-slate-500">
                    {canWrite ? (
                        <>No {title.toLowerCase()} yet. Click <span className="font-mono bg-slate-100 px-1 rounded">+ Add Product</span> above to create one.</>
                    ) : (
                        <>No {title.toLowerCase()} in the catalogue yet.</>
                    )}
                </div>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {items.map((it) => (
                        <li
                            key={it.id}
                            className="px-5 py-3 flex items-center justify-between gap-4"
                            data-testid={`catalogue-row-${it.id}`}
                        >
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-slate-900 truncate">{it.name}</span>
                                    {/* Iter 97.11 — Builder visibility pill. Shows whether
                                        this catalogue row appears in the Commercials
                                        Builder picker (Products only). */}
                                    {it.kind === "product" && it.is_active === false && (
                                        <span
                                            className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500"
                                            title="Soft-archived — hidden from the Commercials Builder picker. Toggle 'Active in Builder' inside the edit dialog to restore."
                                            data-testid={`catalogue-archived-badge-${it.id}`}
                                        >
                                            Archived
                                        </span>
                                    )}
                                    {it.kind === "product" && it.is_active !== false && (it.category || it.builder_modules?.length > 0) && (
                                        <span
                                            className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"
                                            title={`Active in Builder${it.category ? ` · category: ${it.category}` : ""}${it.builder_modules?.length ? ` · ${it.builder_modules.length} modules` : ""}`}
                                            data-testid={`catalogue-builder-badge-${it.id}`}
                                        >
                                            Builder
                                        </span>
                                    )}
                                    {/* Iter 40: downstream usage chip — operator sees blast radius before edit/retire */}
                                    {it.usage && (
                                        <UsageChip itemId={it.id} usage={it.usage} />
                                    )}
                                    {/* Iter C.0 — Package badge surfaces multi-component shape at a glance */}
                                    {it.package_kind === "package" && (
                                        <span
                                            className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-800"
                                            title={`Package · ${(it.components || []).length} component${(it.components || []).length === 1 ? "" : "s"} · default presentation: ${it.default_presentation_mode || "itemized"}`}
                                            data-testid={`catalogue-package-badge-${it.id}`}
                                        >
                                            <Plugs size={10} weight="duotone" />
                                            Package · {(it.components || []).length} component{(it.components || []).length === 1 ? "" : "s"}
                                        </span>
                                    )}
                                </div>
                                {it.subtitle && (
                                    <div className="text-[11px] text-slate-500 mt-0.5">{it.subtitle}</div>
                                )}
                                {/* Iter 49.0 — natural-key code surfaced inline for operator
                                    socialisation with external systems. Shown above the
                                    legacy `id` so it becomes the visual primary identifier. */}
                                {it.code && (
                                    <div
                                        className="text-[10px] text-slate-500 mt-0.5 font-mono flex items-center"
                                        title="Catalogue code (natural key — used for CSV import & external system references)"
                                    >
                                        <span data-testid={`catalogue-code-${it.id}`}>
                                            code: <span className="text-slate-700">{it.code}</span>
                                        </span>
                                        <CopyCodeButton
                                            code={it.code}
                                            label="catalogue code"
                                            testid={`catalogue-code-copy-${it.id}`}
                                        />
                                    </div>
                                )}
                                <div className="text-[10px] text-slate-400 mt-0.5 font-mono">{it.id}</div>
                                {/* Iter C.2.1.CAP — Capability badges on catalogue list */}
                                {(it.capabilities || []).length > 0 && (
                                    <div className="mt-1.5">
                                        <CapabilityBadges capabilities={it.capabilities} size="sm" />
                                    </div>
                                )}
                            </div>
                            <div className="hidden sm:flex flex-col items-end shrink-0 text-right">
                                {it.package_kind === "package" ? (
                                    <>
                                        {/* Iter C.0 — Aggregate package totals; the internal
                                            structure is what matters, but this gives operators
                                            a quick "is this a ₹30L or ₹3L bundle?" glance. */}
                                        <div className="reedos-data text-xs font-semibold text-slate-700" data-testid={`catalogue-package-onetime-${it.id}`}>
                                            One-time ₹{Number((it.components || []).filter(c => c.billing_frequency === "One-time").reduce((s, c) => s + Number(c.default_amount || 0), 0)).toLocaleString("en-IN")}
                                        </div>
                                        <div className="reedos-data text-xs text-slate-500" data-testid={`catalogue-package-recurring-${it.id}`}>
                                            Recurring/yr ₹{Number((it.components || []).filter(c => c.billing_frequency !== "One-time").reduce((s, c) => s + Number(c.default_amount || 0), 0)).toLocaleString("en-IN")}
                                        </div>
                                        <div className="text-[10px] text-slate-400 mt-0.5 capitalize">
                                            Presentation: {it.default_presentation_mode || "itemized"}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="reedos-data text-xs font-semibold text-slate-700">
                                            SW {Number(it.default_software_fee || 0) > 0 ? `₹${Number(it.default_software_fee).toLocaleString("en-IN")}` : "—"}
                                        </div>
                                        <div className="reedos-data text-xs text-slate-500">
                                            AMC {Number(it.default_amc_fee || 0) > 0 ? `₹${Number(it.default_amc_fee).toLocaleString("en-IN")}` : "—"}
                                        </div>
                                        <div className="text-[10px] text-slate-400 mt-0.5">
                                            GST {it.default_gst_percent ?? 18}% · HSN <span className="font-mono text-slate-500">{it.hsn_sac_code || "997331"}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                {canWrite && (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onEdit(it)}
                                            className="h-8 px-2 text-slate-600"
                                            data-testid={`catalogue-edit-${it.id}`}
                                        >
                                            <PencilSimple size={13} />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onRemove(it)}
                                            className="h-8 px-2 text-slate-400 hover:text-rose-600"
                                            data-testid={`catalogue-delete-${it.id}`}
                                        >
                                            <Trash size={13} />
                                        </Button>
                                    </>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}


/**
 * Iter 40 — UsageChip
 *
 * Surfaces downstream-dependency visibility for a catalogue item. Today only
 * counts active proposal templates referencing the item; later iterations
 * extend the same lightweight chip to proposals / POs / AMCs / invoices.
 *
 * Three states:
 *   - count === 0   → muted "Unused" chip; safe to retire/rename without surprise
 *   - count >= 1    → indigo chip; clickable, hover tooltip explains the impact
 *   - usage missing → renders nothing (backwards compat — older list responses
 *                     without ``include_usage`` won't trigger the chip)
 */
function UsageChip({ itemId, usage }) {
    if (!usage) return null;
    const n = Number(usage.templates || 0);
    if (n === 0) {
        return (
            <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border bg-slate-50 text-slate-400 border-slate-200"
                title="Not yet used in any active proposal template — safe to retire or rename"
                data-testid={`catalogue-usage-${itemId}`}
            >
                <Stack size={10} weight="duotone" /> Unused
            </span>
        );
    }
    return (
        <Link
            to="/proposal-templates"
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 transition-colors"
            title={`Used in ${n} active proposal template${n === 1 ? "" : "s"}. Edits here are snapshotted — historical templates stay frozen.`}
            data-testid={`catalogue-usage-${itemId}`}
        >
            <Stack size={10} weight="duotone" />
            Used in {n} template{n === 1 ? "" : "s"}
        </Link>
    );
}
